// src/lib/auth.ts

import Router from 'next/router';
import axios from 'axios';
import {PREFIX_URL} from './vars';
import { clearClientChosen } from './clientSession';

console.log("PREFIX URL is: ",PREFIX_URL);

// Types
interface AuthTokens {
  access: string;
  refresh: string;
}

// Local storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// API endpoints (adjust to match your Django backend)
const LOGIN_URL = '/api/token/';
const REFRESH_URL = '/api/token/refresh/';

// Axios instance with interceptors
const api = axios.create({
  baseURL: PREFIX_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log(api.getUri);

// Add request interceptor to include token in requests
api.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    originalRequest._retry = true;
    
    try {
      // Try to refresh the access token
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
        return api(originalRequest);
      } else {
        // If refresh failed, logout and redirect to login
        logout();
        Router.push('/login');
        return Promise.reject(error);
      }
    } catch (refreshError) {
      // If refresh fails, logout and redirect to login
      logout();
      Router.push('/login');
      return Promise.reject(refreshError);
    }
  }
);

// Change password (authenticated)
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<string> => {
  const response = await api.post<{ message: string }>('/api/auth/change-password/', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data.message;
};

// Authentication functions
export const login = async (username: string, password: string): Promise<boolean> => {
  try {
    console.log('Attempting login for:', username);
    console.log('Login URL:', `${PREFIX_URL}${LOGIN_URL}`);

    const response = await api.post<AuthTokens>(LOGIN_URL, {
      username,
      password
    });

    console.log('Login response status:', response.status);
    console.log('Login response data keys:', Object.keys(response.data));

    // Store tokens
    setTokens(response.data.access, response.data.refresh);
    console.log('Tokens stored, access_token exists:', !!response.data.access);
    return true;
  } catch (error: any) {
    console.error('Login failed:', error);
    console.error('Login error response:', error.response?.data);
    console.error('Login error status:', error.response?.status);
    return false;
  }
};

export const logout = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  clearClientChosen();
};

export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  return !!token;  // Simple check - if token exists, trust it. API will validate.
};

// Token helpers
const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const ACCESS_REFRESH_SKEW_MS = 30_000;

let refreshInFlight: Promise<boolean> | null = null;

const accessTokenExpiresSoon = (token: string): boolean => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof decoded.exp !== 'number') return false;
    return decoded.exp * 1000 - Date.now() <= ACCESS_REFRESH_SKEW_MS;
  } catch {
    return false;
  }
};

const refreshAccessTokenOnce = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await axios.post<{ access: string }>(
      `${PREFIX_URL}${REFRESH_URL}`,
      { refresh: refreshToken }
    );

    localStorage.setItem(ACCESS_TOKEN_KEY, response.data.access);
    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

export const refreshAccessToken = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessTokenOnce().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const forceLogout = (): void => {
  logout();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

export async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const send = async (): Promise<Response> => {
    const token = getAccessToken();
    if (token && accessTokenExpiresSoon(token)) {
      await refreshAccessToken();
    }

    const headers = new Headers(init.headers);
    const current = getAccessToken();
    if (current) headers.set('Authorization', `Bearer ${current}`);
    if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
      headers.delete('Content-Type');
    }
    return fetch(input, { ...init, headers });
  };

  let response = await send();
  if (response.status !== 401) return response;

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    forceLogout();
    return response;
  }

  response = await send();
  if (response.status === 401) {
    forceLogout();
  }
  return response;
}

// Export API instance for use in components/pages
export { api };